import { useState, useEffect, useCallback } from 'react'
import { listClients, listPayables } from '@/lib/revfinops-api'
import type { Activity } from '@/types'

interface UseRecentActivitiesReturn {
  activities: Activity[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

/**
 * Custom hook to fetch and aggregate recent activities from RevFinOps APIs
 * Combines data from clients, payables, and assignments to create a unified activity feed
 *
 * @returns Activities, loading state, error state, and refresh function
 */
export function useRecentActivities(): UseRecentActivitiesReturn {
  const [activities, setActivities] = useState<Activity[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch data from multiple sources in parallel
      const [clientsResult, payablesResult] = await Promise.all([
        listClients(),
        listPayables()
      ])

      const allActivities: Activity[] = []

      // Transform clients into activities
      if (clientsResult.success && clientsResult.data) {
        const clientActivities: Activity[] = clientsResult.data
          .slice(0, 5) // Limit to recent 5 clients
          .map(client => ({
            id: `client-${client.id}`,
            type: 'client_created' as const,
            timestamp: client.createdAt,
            entityId: client.id,
            entityName: client.name,
            actor: 'Sistema', // Default actor, can be enhanced with createdBy field
            description: `Criou cliente ${client.name}`
          }))

        allActivities.push(...clientActivities)
      }

      // Transform payables into activities
      if (payablesResult.success && payablesResult.data) {
        const payableActivities: Activity[] = payablesResult.data
          .filter(p => p.status !== 'pending') // Only show processed payables
          .slice(0, 5) // Limit to recent 5 payables
          .map(payable => {
            // Determine activity type based on payable status
            let type: Activity['type'] = 'payable_approved'
            let description = `Aprovou pagamento de R$ ${payable.amount.toLocaleString('pt-BR')}`
            let timestamp = payable.approvedDate || payable.updatedAt

            if (payable.status === 'scheduled') {
              type = 'payment_scheduled'
              description = `Agendou pagamento de R$ ${payable.amount.toLocaleString('pt-BR')}`
              timestamp = payable.scheduledDate || payable.updatedAt
            } else if (payable.status === 'paid') {
              type = 'payment_completed'
              description = `Completou pagamento de R$ ${payable.amount.toLocaleString('pt-BR')}`
              timestamp = payable.paidDate || payable.updatedAt
            }

            return {
              id: `payable-${payable.id}`,
              type,
              timestamp,
              entityId: payable.id,
              entityName: payable.clientName || `Pagamento ${payable.id.slice(0, 8)}`,
              actor: payable.approvedBy || 'Sistema',
              description
            }
          })

        allActivities.push(...payableActivities)
      }

      // TODO: Add assignment activities when assignment API is integrated
      // For now, we only have clients and payables

      // Merge all activities and sort by timestamp (most recent first)
      const sortedActivities = allActivities
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 10) // Limit to last 10 activities

      setActivities(sortedActivities)
    } catch (err) {
      setError('Erro ao carregar atividades recentes')
      // Error is already captured in error state for UI display
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial load
  useEffect(() => {
    loadActivities()
  }, [loadActivities])

  return {
    activities,
    loading,
    error,
    refresh: loadActivities
  }
}
